/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { BV_AUTH_CALLBACK_URL, BV_BASE_URL, BV_PASS_KEY } from '@/features/reviews/constants';
import type {
  ProgressiveSubmissionResponse,
  SubmissionFields,
  SubmitReviewResponseError,
} from '../interface/types';
import { getReviewsCampaignID } from '@/features/reviews/utils/getReviewsCampaignID';
import type { SubmitReviewPageType } from '../../reviews/interface';
import type { BVBoolean } from '../../reviews/interface/bv-types';
import { localStorage } from '@/utils/localStorage';
import { trackRatingOnlyReview } from '../utils/trackRatingOnlyReview';
import { useNotification } from '@az/starc-ui';
import { useLabels } from '@/hooks/useLabels';
import logger from '@/utils/logger';
import { isRetryableBVError } from '../utils/retryStrategies';

const URL = `${BV_BASE_URL}/data/progressiveSubmit.json`;

export type ProgressiveSubmissionOptions = {
  locale: string;
  productId: string;
  submissionSessionToken?: string;
  submissionFields: SubmissionFields;
  fp?: string;
  pageType: SubmitReviewPageType;
  userId?: string;
  userToken?: string;
  verifiedPurchaser?: BVBoolean;
  hostedauth?: boolean;
  displaycode?: string;
};

const postProgressiveSubmission = async ({
  locale,
  productId,
  userId,
  submissionSessionToken,
  submissionFields,
  fp,
  pageType,
  userToken,
  verifiedPurchaser,
  hostedauth,
  displaycode,
}: ProgressiveSubmissionOptions) => {
  if (!productId) {
    throw new Error('productIds is required');
  }

  if (hostedauth && !userId) {
    throw new Error('userId is required when using hostedauth');
  }

  const params = {
    ApiVersion: 5.4,
    passkey: BV_PASS_KEY,
    ...(hostedauth ? { hostedauth } : {}),
    ...(displaycode ? { displaycode } : {}),
  };

  const sanitizedSubmissionFields: SubmissionFields = { ...submissionFields };
  delete (sanitizedSubmissionFields as Partial<SubmissionFields>).media;

  const campaignId = getReviewsCampaignID(pageType);

  const payload = {
    locale: locale.replace('-', '_'),
    productId,
    submissionSessionToken,
    deviceFingerprint: fp,
    campaignId,
    submissionFields: {
      ...sanitizedSubmissionFields,
      sendEmailAlertWhenCommented: true,
      agreedtotermsandconditions: true,
      sendEmailAlertWhenPublished: true,
      sendEmailAlertWhenAnswered: true,
      contextdatavalue_VerifiedPurchaser: verifiedPurchaser,
      useremail: submissionFields.useremail?.toLowerCase(),
      ...(hostedauth
        ? {
            hostedAuthentication_callbackUrl: BV_AUTH_CALLBACK_URL,
            hostedauthentication_authenticationemail: submissionFields.useremail,
          }
        : {}),
    },
    ...(hostedauth ? { userId } : { userToken }),
  };

  const response = await axios.post<ProgressiveSubmissionResponse>(URL, payload, {
    params,
  });

  if (response.data.hasErrors && response.data.errors) {
    const errorCodes = response.data.errors.map((error) => error.code).join(', ');
    const error = new Error(`Progressive submission failed: ${errorCodes}`);
    Object.assign(error, {
      response: {
        data: response.data,
      },
    });
    throw error;
  }

  return response.data;
};

type ProgressiveSubmissionMutationProps = {
  onSuccess?: (
    data: ProgressiveSubmissionResponse,
    variables: ProgressiveSubmissionOptions,
    context: unknown,
  ) => void;
  onError?: (
    error: SubmitReviewResponseError,
    variables: ProgressiveSubmissionOptions,
    context: unknown,
  ) => void;
};

const labelMap = {
  errorDuringSubmissionTitle: 'label_reviews_submit_errorDuringSubmissionTitle',
  errorDuringSubmissionText: 'label_reviews_submit_errorDuringSubmissionText',
};

export const useProgressiveSubmissionMutation = (props?: ProgressiveSubmissionMutationProps) => {
  const { onSuccess, onError } = props ?? {};

  const notification = useNotification();
  const labels = useLabels(labelMap);

  return useMutation<
    ProgressiveSubmissionResponse,
    SubmitReviewResponseError,
    ProgressiveSubmissionOptions
  >({
    mutationFn: postProgressiveSubmission,
    onSuccess: (data, variables, context) => {
      const { isratingsonly, rating } = variables.submissionFields;

      if (isratingsonly && rating) {
        trackRatingOnlyReview(rating, [variables.productId]);
      }

      const submissionToken = data.response.authorSubmissionToken;
      if (submissionToken) {
        localStorage.setItem('reviewSubmissionId', submissionToken);
      }
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if ('response' in error && error.response) {
        const hasErrors = error.response.data?.hasErrors;
        if (hasErrors) {
          const allErrors = error.response.data?.errors;
          const errors = allErrors?.map((error) => error.code).join(', ') ?? '';
          const allErrorsAreDuplicates = allErrors?.every(
            (error) => error.code === 'ERROR_DUPLICATE_SUBMISSION',
          );
          logger.error({
            message: 'Failed to submit review progression',
            meta: {
              source: 'postProgressiveSubmission',
              errors,
            },
          });

          if (!allErrorsAreDuplicates) {
            const id = notification.show({
              type: 'error',
              title: labels.errorDuringSubmissionTitle,
              text: labels.errorDuringSubmissionText,
              position: 'top-end',
              hasCloseButton: true,
              onClose: () => notification.hide(id),
            });
          }
        }
      } else if (error instanceof Error) {
        logger.error({ message: error.message });
      }
      onError?.(error, variables, context);
    },
    retry: isRetryableBVError,
  });
};
